'use strict';
import Manager from '@jsclasses/menu/manager';
import Desktop from '@jsclasses/menu/desktop';
import Mobile from '@jsclasses/menu/mobile';
import { listenDOMEvent, createElement } from '@jspartials/dom-api-utils';

import {
  classes,
  addClassToElement,
  removeClassToElement,
  hasClassElement
} from '@jspartials/class-list-utils';

import { debounce } from '@jspartials/general-utils';
import {
  mediaQuery,
  getMediaQuery,
  isPointingDevice
} from '@jspartials/general-utils';

var menuManager = null;
var menuDesktop = null;
var menuMobile = null;

const mediaQueryTablet = getMediaQuery(mediaQuery.tablet); //min-width: 768px
const selector = {
  menuSessionStorageKey: 'hw-menu',
  menuSubmenuClass: 'hw-menu__submenu',
  buttonCategory: '.js-menu-mobile__category',
  buttonCloseMenuMobile: '.js-menu-mobile__close-menu-button',
  buttonCloseMenuMobileClass: 'js-menu-mobile__close-menu-button',
  buttonCloseSubmenuMobile: '.js-menu-mobile__close-submenu-button',
  buttonCloseSubmenuMobileClass: 'js-menu-mobile__close-submenu-button'
};

const debounceSubmenuOpening = debounce(addActiveStateItemFunctionality, 150);

const chevronLeftIcon = `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M12.452 4.516c.446.436.481 1.043 0 1.576L8.705 10l3.747 3.908c.481.533.446 1.141 0 1.574-.445.436-1.197.408-1.615 0-.418-.406-4.502-4.695-4.502-4.695a1.095 1.095 0 0 1 0-1.576s4.084-4.287 4.502-4.695c.418-.409 1.17-.436 1.615 0z"/>
      </svg>`;

const chevronRightIcon = `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M9.163 4.516c.418.408 4.502 4.695 4.502 4.695a1.095 1.095 0 0 1 0 1.576s-4.084 4.289-4.502 4.695c-.418.408-1.17.436-1.615 0-.446-.434-.481-1.041 0-1.574L11.295 10 7.548 6.092c-.481-.533-.446-1.141 0-1.576.445-.436 1.197-.409 1.615 0z"/>
      </svg>`;

const closeIcon = `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path clip-rule="evenodd" fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"></path>
      </svg>`;

const removeSubmenuButtons = (submenuAssetId) => {
  const submenus = menuMobile.contentElement.querySelectorAll(
    `[data-submenu-asset-id="${submenuAssetId}"]`
  );
  if (!submenus || submenus.length === 0) return;
  submenus.forEach((submenu) => {
    submenu.remove();
  });
};

const showSubmenuIconsAndButtons = () => {
  addClassToElement(menuDesktop.header, 'md:tw-flex');
  removeClassToElement(menuMobile.openButtonElement, classes.hidden);
  addClassToElement(menuMobile.openButtonElement, 'tw-flex');
};

const isValidCategory = (category) => {
  return (
    category.options &&
    category.options.is_visible === true &&
    category.title &&
    category.title.text.trim() !== '' &&
    category.link &&
    category.link.url &&
    category.link.url.trim() !== ''
  );
};

/**
 * Create menu element and content
 * @param {string} genderId - Gender ID
 * @returns {void}
 */
const createMenu = (genderId) => {
  const unparsedMenuInfo = sessionStorage.getItem(
    selector.menuSessionStorageKey
  );
  if (!unparsedMenuInfo) return;
  const rawMenuInfo = JSON.parse(unparsedMenuInfo);
  const menuInfo = rawMenuInfo.find((menu) => menu.gender.id === genderId);
  if (!menuInfo) return;

  const activeMenu = menuManager.getActiveMenu().wrapperCategoriesElement;

  activeMenu.innerHTML = ''; // Clear the menu
  activeMenu.appendChild(generateMenuLinks(menuInfo.items));
  menuManager.getActiveMenu()?.animateCategories();

  if (menuManager.isMobileMenuActive()) {
    menuMobile.categoryButtons = document.querySelectorAll(
      selector.buttonCategory
    );
    menuMobile.categoryButtons.forEach((buttonCategory) => {
      listenDOMEvent(buttonCategory, 'click', (e) => {
        e.stopPropagation();
        menuMobile.openSubmenu(e.currentTarget);
      });
    });
  }
};

/**
 * Create submenu element and content
 * @param {HTMLElement} parentElement - Parent element
 * @param {HTMLElement} button - Button element
 * @returns {void}
 */
const createSubmenu = (parentElement, button) => {
  const submenuAssetId = button.dataset.submenuAssetId;

  if (!button || !submenuAssetId || submenuAssetId === 'null') return;
  const submenuTitle = button.dataset.submenuTitle;
  const sessionStorageItem = sessionStorage.getItem(submenuAssetId);
  const submenuInfo = sessionStorageItem
    ? JSON.parse(sessionStorageItem.replace(/&quot;/g, '"'))
    : null;

  if (!submenuInfo || !submenuInfo.items || !submenuInfo.items.length > 0) {
    removeClassToElement(menuDesktop.contentElement, classes.is.open);
    return;
  }

  // Add desktop column
  if (menuManager.isDesktopMenuActive()) {
    addClassToElement(menuDesktop.contentElement, classes.is.open);
  }

  // Create submenu content elements
  const activeSubmenuContent = document.createDocumentFragment();
  const header = document.createDocumentFragment();
  const submenuList = document.createDocumentFragment();

  // Fill submenu content
  menuManager.isMobileMenuActive() &&
    header.appendChild(generateSubmenuHeader(submenuTitle));
  submenuList.appendChild(generateSubmenuLinks(submenuInfo));
  activeSubmenuContent.appendChild(header);
  activeSubmenuContent.appendChild(submenuList);

  // Determine the target menu object
  var activeMenu = menuManager.getActiveMenu();

  // Create the new submenu element
  var updatedActiveSubmenu = createElement({
    tag: 'div',
    content: activeSubmenuContent,
    classes: [selector.menuSubmenuClass, classes.is.visible]
  });

  // Update the activeSubmenu property of the target menu
  activeMenu.activeSubmenu = updatedActiveSubmenu;

  // Append the submenu to the parent element
  parentElement.appendChild(updatedActiveSubmenu);

  // Add close submenu functionality to mobile menu
  if (menuManager.isMobileMenuActive()) {
    const buttonCloseMenuMobile = updatedActiveSubmenu.querySelector(
      selector.buttonCloseMenuMobile
    );

    menuMobile.closeSubmenuButtonElement =
      document.querySelector(selector.buttonCloseSubmenuMobile) || null;
    // Go back functionality submenu
    listenDOMEvent(menuMobile.closeSubmenuButtonElement, 'click', () => {
      menuMobile.closeSubmenu();
    });

    // Close submenu functionality
    listenDOMEvent(buttonCloseMenuMobile, 'click', () => {
      menuMobile.close();
    });
  }
};

const generateMenuLinks = (menuInfo) => {
  if (!menuInfo) return document.createDocumentFragment();
  var menuContent = document.createDocumentFragment();

  menuInfo.forEach((item) => {
    if (!isValidCategory(item)) return;
    const hasNewTag = item.options.is_new;
    const hasSubtitleTag =
      item.subtitle && item.subtitle.text && item.subtitle.text.trim() !== '';
    var itemContent = document.createDocumentFragment();
    var submenuInfo = sessionStorage.getItem(item.submenuAssetId)
      ? JSON.parse(sessionStorage.getItem(item.submenuAssetId))
      : null;

    if (isSubmenuVisible(submenuInfo) && menuManager.isMobileMenuActive()) {
      itemContent = createElement({
        tag: 'button',
        content: `
        <span class="tw-block tw-w-4 tw-h-4">
          ${chevronRightIcon}
        <span>`,
        attributes: [
          {
            name: 'class',
            value: 'js-menu-mobile__category hw-menu-mobile__category '
          },
          {
            name: 'data-submenu-title',
            value: item.title.text
          },
          {
            name: 'data-submenu-asset-id',
            value: item.submenuAssetId
          },
          {
            name: 'type',
            value: 'button'
          }
        ]
      });
    }

    const itemText = createElement({
      tag: 'span',
      content: item.title.text,
      attributes: [
        {
          name: 'style',
          value: `
            --color-text: ${item.title.color || 'initial'};
            --font-weight: ${item.title.weight || 'initial'};
            `
        },
        {
          name: 'class',
          value: 'hw-menu__link-text'
        }
      ]
    });

    const itemLink = createElement({
      tag: 'a',
      content: itemText,
      attributes: [
        {
          name: 'href',
          value: item.link.url
        },
        {
          name: 'id',
          value: item.id
        },
        {
          name: 'class',
          value: 'js-menu-link hw-menu-link'
        },
        {
          name: 'target',
          value: item.link.target
        }
      ]
    });

    if (hasNewTag) {
      const itemNewTag = createElement({
        tag: 'span',
        content: 'NEW',
        attributes: [
          {
            name: 'class',
            value: 'hw-menu__tag'
          }
        ]
      });
      itemLink.appendChild(itemNewTag);
    }

    if (hasSubtitleTag) {
      const itemSubtitleTag = createElement({
        tag: 'span',
        content: item.subtitle.text,
        attributes: [
          {
            name: 'class',
            value: 'hw-menu__tag hw-menu__subtitle-tag'
          },
          {
            name: 'style',
            value: `
              --color-text: ${item.subtitle.color || 'initial'};
              --bg-color-text: ${item.subtitle.background_color || 'initial'};
              `
          }
        ]
      });
      itemLink.appendChild(itemSubtitleTag);
    }

    const itemContentWrapper = createElement({
      tag: 'li',
      content: itemLink,
      attributes: [
        {
          name: 'class',
          value: `tw-relative tw-uppercase ${
            menuManager.isDesktopMenuActive() && 'js-menu-desktop__item'
          } hw-menu__link-wrapper`
        }
      ]
    });

    if (menuManager.isDesktopMenuActive()) {
      itemContentWrapper.setAttribute(
        'data-submenu-asset-id',
        item.submenuAssetId
      );
      itemContentWrapper.setAttribute('data-open', 'false');
    }

    addActiveStateItem(
      menuDesktop.wrapperCategoriesElement,
      itemContentWrapper,
      menuDesktop.openSubmenu.bind(menuDesktop)
    );

    itemContentWrapper.appendChild(itemContent);
    menuContent.appendChild(itemContentWrapper);
  });

  return menuContent;
};

const generateSubmenuHeader = (submenuTitle) => {
  const activeGenderText = menuMobile.getActiveGenderText();

  const headerElement = createElement({
    tag: 'header',
    content: '',
    attributes: [
      {
        name: 'class',
        value:
          'tw-relative tw-p-3 tw-pr-11 tw-pl-12 tw-border-b tw-border-gray-800 tw-mt-[1px]'
      }
    ]
  });

  const buttonCloseSubmenuMobile = createElement({
    tag: 'button',
    content: `
    <span class="tw-block tw-w-5 tw-h-5 tw-mb-[1px]">
      ${chevronLeftIcon}
    </span>`,
    attributes: [
      {
        name: 'class',
        value: `tw-flex tw-h-full tw-w-[calc(100%-44px)] tw-absolute tw-pl-4 ${selector.buttonCloseSubmenuMobileClass} tw-left-0 tw-top-0 tw-border-none tw-items-center tw-justify-start tw-min-w-[40px] `
      }
    ]
  });

  const headerTitleWrapper = createElement({
    tag: 'p',
    content: '',
    attributes: [
      {
        name: 'class',
        value: 'tw-flex tw-items-baseline tw-gap-2'
      }
    ]
  });

  const headerTitle = createElement({
    tag: 'span',
    content: submenuTitle,
    attributes: [
      {
        name: 'class',
        value: 'tw-font-semibold tw-text-sm'
      }
    ]
  });

  const headerActiveGender = createElement({
    tag: 'span',
    content: activeGenderText,
    attributes: [
      {
        name: 'class',
        value: 'tw-font-medium tw-text-xs tw-text-gray-300'
      }
    ]
  });

  const buttonCloseMenu = createElement({
    tag: 'button',
    content: `
    <span class="tw-block tw-w-5 tw-h-5">
      ${closeIcon}
    </span>`,
    attributes: [
      {
        name: 'class',
        value: `tw-flex tw-absolute tw-h-full ${selector.buttonCloseMenuMobileClass} tw-right-0 tw-top-0 tw-border-none tw-items-center tw-justify-center tw-aspect-square`
      }
    ]
  });

  headerElement.appendChild(buttonCloseSubmenuMobile);
  headerElement.appendChild(headerTitleWrapper);
  headerTitleWrapper.appendChild(headerTitle);
  headerTitleWrapper.appendChild(headerActiveGender);
  headerElement.appendChild(buttonCloseMenu);

  return headerElement;
};

const generateSubmenuLinks = (submenuInfo) => {
  if (!submenuInfo.items) return document.createDocumentFragment();

  const list = createElement({
    tag: 'ul',
    content: '',
    attributes: [
      {
        name: 'class',
        value: 'hw-menu__submenu-list'
      }
    ]
  });

  submenuInfo.items.forEach((item) => {
    if (!isValidCategory(item)) return;

    const hasNewTag = item.options.is_new;
    const hasSubtitleTag =
      item.subtitle && item.subtitle.text && item.subtitle.text.trim() !== '';
    const itemLi = createElement({
      tag: 'li',
      content: '',
      attributes: [
        {
          name: 'class',
          value: 'hw-menu__link-wrapper'
        }
      ]
    });

    const itemText = createElement({
      tag: 'span',
      content: item.title.text,
      attributes: [
        {
          name: 'style',
          value: `
            --color-text: ${item.title.color || 'initial'};
            --font-weight: ${item.title.weight || 'initial'};
            `
        },
        {
          name: 'class',
          value: 'hw-menu__link-text'
        }
      ]
    });

    const itemLink = createElement({
      tag: 'a',
      content: itemText,
      attributes: [
        {
          name: 'href',
          value: item.link.url
        },
        {
          name: 'target',
          value: item.link.target
        },
        {
          name: 'class',
          value: 'hw-submenu-link'
        }
      ]
    });

    const itemNewTag = createElement({
      tag: 'span',
      content: 'NEW',
      attributes: [
        {
          name: 'class',
          value: 'hw-menu__tag'
        }
      ]
    });

    const itemSubtitleTag = createElement({
      tag: 'span',
      content: item.subtitle.text,
      attributes: [
        {
          name: 'class',
          value: 'hw-menu__tag hw-menu__subtitle-tag'
        },
        {
          name: 'style',
          value: `
              --color-text: ${item.subtitle.color || 'initial'};
              --bg-color-text: ${item.subtitle.background_color || 'initial'};
            `
        }
      ]
    });

    addActiveStateItem(list, itemLi);
    hasNewTag && itemLink.appendChild(itemNewTag);
    hasSubtitleTag && itemLink.appendChild(itemSubtitleTag);
    itemLi.appendChild(itemLink);

    list.appendChild(itemLi);
  });

  return list;
};

const isSubmenuVisible = (submenuAssetsInfo) => {
  return submenuAssetsInfo && submenuAssetsInfo.items;
};
function addActiveStateItem(wrapper, item, openSubmenuDesktop) {
  listenDOMEvent(item, 'mouseenter', (e) => {
    wrapper.querySelectorAll('li').forEach((menuItem) => {
      toggleActiveState(item, menuItem);
    });
    debounceSubmenuOpening(item, openSubmenuDesktop);
  });
}

const toggleActiveState = (itemLi, item) => {
  if (itemLi !== item) {
    addClassToElement(item, classes.is.invalid);
    removeClassToElement(item, classes.is.active);
    item.setAttribute('data-open', 'false');
  } else {
    removeClassToElement(item, classes.is.invalid);
    addClassToElement(item, classes.is.active);
  }
};

function addActiveStateItemFunctionality(item, openSubmenuDesktop) {
  if (
    hasClassElement(item, classes.is.active) &&
    item.getAttribute('data-open') === 'false' &&
    openSubmenuDesktop
  ) {
    openSubmenuDesktop(item);
    item.setAttribute('data-open', true);
  }
}

function initManager() {
  menuDesktop = new Desktop();
  menuMobile = new Mobile();

  menuManager = new Manager(menuDesktop, menuMobile); // Tracks the active menu

  // Update menu context on window resize
  mediaQueryTablet.addEventListener('change', function (mm) {
    if (!isPointingDevice()) {
      menuManager.setActiveMenu(menuMobile);
    } else {
      menuManager.setActiveMenu(mm.matches ? menuDesktop : menuMobile);
    }
  });
}

export {
  createMenu,
  createSubmenu,
  toggleActiveState,
  addActiveStateItem,
  initManager,
  isSubmenuVisible,
  showSubmenuIconsAndButtons,
  removeSubmenuButtons
};
